import type { LocationFeatureDetails } from '../../types/Location';

/**
 * Shared data shape for the multi-step Create / Edit Location wizard.
 *
 * The wizard collects data across five steps (Info → Location Settings →
 * Manager → Features → Pricing). This interface is the single source of truth
 * for the data shared between steps. Fields beyond Step 1 are optional here and
 * will be populated by later steps.
 */
export interface WizardLocationFeature {
  feature?: string;
  details?: LocationFeatureDetails | null;
}

export interface LocationManagerData {
  username: string;
  firstName: string;
  lastName: string;
  phoneNumber: string;
  isOptedIntoText: boolean;
  /**
   * SMS deliverability for this manager's number. `false` means Twilio Lookup explicitly
   * rejected it, so texts will not be delivered even though the manager is opted in.
   * `null`/undefined means no verification has been attempted.
   */
  smsVerified?: boolean | null;
}

/**
 * Details typed into the inline "+ Add New Manager" form on Step 3. The user is only
 * created (and assigned as the location's manager) when the step is saved.
 */
export interface NewLocationManagerData {
  firstName: string;
  lastName: string;
  phoneNumber: string;
  /** Email address; becomes the new user's username. */
  email: string;
  isOptedIntoText: boolean;
}

export interface LocationEquipmentTypeData {
  id?: string | null;
   /**
   * Client-only stable identity for React list keys. Not sent to the API — both
   * the save payload and load mapping pick fields explicitly, so this never leaks.
   */
  _key?: string;
  equipmentType: string;
  pricePerDay: number | null;
  pricePerWeek: number | null;
  pricePerMonth: number | null;
  /**
   * Optional overage rate (cents) for this equipment type. Blank / 0 falls back
   * to the location's overage rate. Optional so existing callers can omit it.
   */
  overageRate?: number | null;
}

export interface LocationWizardData {
  /** Server-assigned id. Empty/undefined until the first draft save. */
  id?: string;

  // --- Step 1: Location Information ---
  locationName: string;
  /** Human-readable address string shown in the address field (the lookup value). */
  locationAddress: string;
  addressLine1: string | null;
  addressLine2: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
  addressLatitude: number | null;
  addressLongitude: number | null;
  /**
   * True only when the user explicitly chose "Your entry" over Google's
   * suggestion in the address-confirmation modal. False in every other case:
   * dropdown-selected, silently auto-matched, or user picked Google's suggestion.
   */
  addressManuallyConfirmed: boolean;
  phoneNumber: string;
  listingImageFileName: string;
  /** Existing gallery file names (already persisted on the location record). */
  gallery: { fileName: string }[];

  // --- Step 2: Location Settings ---
  locationDescription: string;
  instructions: string;
  has247Access: boolean;
  /** Hours the gate is staffed/accessible to buyers. Only relevant when has247Access is false. */
  gateHours: string;
  /** Instructions for accessing the facility outside of gate hours. Only relevant when has247Access is false. */
  afterHoursAccessProcedure: string;
  minNumberOfSpaces: number | null;
  totalNumberOfSpaces: number | null;
  managedLocation: boolean;
  /** Number of spaces eligible for automated approval (when managedLocation is on). */
  managedSpaces: number | null;

  // --- Step 3: Location Manager ---
  /** The selected location manager object, or null if none is assigned. */
  locationManager: LocationManagerData | null;
  /**
   * Non-null while "+ Add New Manager" is selected. Mutually exclusive with `locationManager`:
   * picking an existing manager (or Choose) resets it to null, discarding anything typed.
   */
  newLocationManager: NewLocationManagerData | null;

  /**
   * Location feature tags (e.g. 24/7 Access, Paved, Security). Populated by the Features step.
   * Omitting `details` keeps what's saved server-side; `{}` clears it.
   */
  locationFeatures: WizardLocationFeature[];

  // --- Step 5: Pricing ---
  pricePerDay: number | null;
  pricePerMonth: number | null;
  overageRate: number | null;
  chargeOverages: boolean;
  qrScannerAvailable: boolean;
  locationEquipmentTypes: LocationEquipmentTypeData[];

  /** Whether the listing is published. Drafts are always live = false. */
  live: boolean;
}

/**
 * The five steps every supplier sees, in order.
 * Admins additionally see a sixth "Admin Settings" step (see {@link getWizardSteps}).
 */
export const SUPPLIER_WIZARD_STEPS = [
  'Info',
  'Location Settings',
  'Manager',
  'Features',
  'Pricing',
] as const;

/** Label for the admin-only sixth step (Charge Overages, Location Live, QR Scanner). */
export const ADMIN_SETTINGS_STEP_LABEL = 'Admin Settings';

/**
 * Backwards-compatible alias for the supplier step list. Prefer
 * {@link getWizardSteps} so the wizard renders the correct number of steps for
 * the current user's role.
 */
export const WIZARD_STEPS = SUPPLIER_WIZARD_STEPS;

export type WizardStepLabel =
  | typeof SUPPLIER_WIZARD_STEPS[number]
  | typeof ADMIN_SETTINGS_STEP_LABEL;

/** Zero-based index of the admin-only Admin Settings step (only present for admins). */
export const ADMIN_SETTINGS_STEP_INDEX = SUPPLIER_WIZARD_STEPS.length;

/**
 * Zero-based indices for each named step, derived from `SUPPLIER_WIZARD_STEPS`
 * so they can never silently drift out of sync if the step order changes.
 * Prefer these over numeric literals whenever code outside the step
 * indicator itself needs to refer to a specific step — e.g. the admin
 * "Autofill Location" enrichment marks Info/Settings/Features dirty by index
 * (see `applyEnrichmentData` in `useCreateLocationWizard.ts`).
 */
export const WIZARD_STEP_INFO = SUPPLIER_WIZARD_STEPS.indexOf('Info');
export const WIZARD_STEP_LOCATION_SETTINGS = SUPPLIER_WIZARD_STEPS.indexOf('Location Settings');
export const WIZARD_STEP_MANAGER = SUPPLIER_WIZARD_STEPS.indexOf('Manager');
export const WIZARD_STEP_FEATURES = SUPPLIER_WIZARD_STEPS.indexOf('Features');
export const WIZARD_STEP_PRICING = SUPPLIER_WIZARD_STEPS.indexOf('Pricing');


/**
 * Returns the ordered list of step labels for the wizard. SecurSpace admins get
 * an extra "Admin Settings" step (6 total); everyone else gets the standard 5.
 * Role-based step injection lives here at the wizard-controller level so the
 * step-indicator component stays presentational.
 */
export const getWizardSteps = (isAdmin: boolean): WizardStepLabel[] =>
  isAdmin
    ? [...SUPPLIER_WIZARD_STEPS, ADMIN_SETTINGS_STEP_LABEL]
    : [...SUPPLIER_WIZARD_STEPS];

export const createEmptyLocationWizardData = (): LocationWizardData => ({
  id: '',
  locationName: '',
  locationAddress: '',
  addressLine1: null,
  addressLine2: null,
  city: null,
  state: null,
  zip: null,
  addressLatitude: null,
  addressLongitude: null,
  addressManuallyConfirmed: false,
  phoneNumber: '',
  listingImageFileName: '',
  gallery: [],
  locationDescription: '',
  instructions: '',
  has247Access: true,
  gateHours: '',
  afterHoursAccessProcedure: '',
  minNumberOfSpaces: null,
  totalNumberOfSpaces: null,
  managedLocation: false,
  managedSpaces: null,
  locationManager: null,
  newLocationManager: null,
  locationFeatures: [],
  pricePerDay: null,
  pricePerMonth: null,
  overageRate: null,
  chargeOverages: false,
  qrScannerAvailable: false,
  locationEquipmentTypes: [],
  live: false,
});

/**
 * Generates a stable client-only key for an equipment-type row, used as the React
 * list key so removing a row never re-associates inputs with the wrong row.
 * Uses `crypto.randomUUID` when available, falling back to a timestamp + counter.
 */
let equipmentRowKeyCounter = 0;
export const newEquipmentRowKey = (): string => {
  const cryptoObj = typeof crypto !== 'undefined' ? crypto : undefined;
  if (cryptoObj?.randomUUID) {
    return cryptoObj.randomUUID();
  }
  equipmentRowKeyCounter += 1;
  return `eq-${Date.now()}-${equipmentRowKeyCounter}`;
};

